import React, { useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';

const IncomeStatement = () => {
  const [reportType, setReportType] = useState('standard');
  const now = new Date();
  const [periodStart, setPeriodStart] = useState(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0]);
  const [periodEnd, setPeriodEnd] = useState(now.toISOString().split('T')[0]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const handlePreview = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/accounting-reports/income-statement?period_start=${periodStart}&period_end=${periodEnd}&by_month=${reportType === 'by_month'}`);
      setData(res.data);
    } catch (error) {
      toast.error('Failed to load income statement');
    } finally {
      setLoading(false);
    }
  };

  const fmt = (v) => Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const sectionStyle = { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '20px', marginBottom: '16px' };
  const rowStyle = { display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '14px', borderBottom: '1px solid #f1f5f9' };
  const totalRowStyle = { ...rowStyle, fontWeight: 700, borderTop: '2px solid #e2e8f0', borderBottom: 'none', paddingTop: '10px' };

  return (
    <div className="card" style={{ padding: '24px', maxWidth: '800px' }}>
      <h2 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '20px' }}>Income Statement</h2>

      {/* Filters */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ width: '80px', fontSize: '13px', fontWeight: 600 }}>Type</label>
          <label style={{ fontSize: '13px', cursor: 'pointer' }}>
            <input type="radio" value="standard" checked={reportType === 'standard'} onChange={() => setReportType('standard')} /> Standard
          </label>
          <label style={{ fontSize: '13px', cursor: 'pointer', marginLeft: '16px' }}>
            <input type="radio" value="by_month" checked={reportType === 'by_month'} onChange={() => setReportType('by_month')} /> By Month
          </label>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ width: '80px', fontSize: '13px', fontWeight: 600 }}>Period</label>
          <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }} />
          <span>~</span>
          <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        <button onClick={handlePreview} disabled={loading}
          style={{ padding: '8px 20px', backgroundColor: '#5f9ea0', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}>
          {loading ? 'Loading...' : 'Preview'}
        </button>
      </div>

      {/* Disclaimer */}
      <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px', padding: '16px', marginBottom: '20px', fontSize: '12px', color: '#1e40af' }}>
        <strong>Information Disclaimer</strong><br />
        The information provided is for general informational purposes only and should not be considered professional financial advice.
        It is your responsibility to verify the accuracy of the data.
      </div>

      {data && data.type === 'standard' && (
        <div>
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 700 }}>Income Statement</h3>
            <p style={{ fontSize: '13px', color: '#64748b' }}>
              {new Date(data.period_start).toLocaleDateString()} ~ {new Date(data.period_end).toLocaleDateString()}
            </p>
          </div>

          <div style={sectionStyle}>
            <div style={rowStyle}><span style={{ fontWeight: 600 }}>Revenue (A/R)</span><span>{fmt(data.revenue)}</span></div>
            <div style={rowStyle}><span style={{ fontWeight: 600 }}>Cost of Services (A/P)</span><span>{fmt(data.cost_of_services)}</span></div>
            <div style={totalRowStyle}>
              <span>Gross Profit</span>
              <span style={{ color: data.gross_profit >= 0 ? '#059669' : '#dc2626' }}>{fmt(data.gross_profit)}</span>
            </div>
          </div>

          <div style={{ ...sectionStyle, background: data.net_income >= 0 ? '#f0fdf4' : '#fef2f2' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: 700 }}>
              <span>Net Income</span>
              <span style={{ color: data.net_income >= 0 ? '#059669' : '#dc2626' }}>{fmt(data.net_income)}</span>
            </div>
          </div>
        </div>
      )}

      {data && data.type === 'by_month' && (
        <div>
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 700 }}>Income Statement (By Month)</h3>
            <p style={{ fontSize: '13px', color: '#64748b' }}>
              {new Date(data.period_start).toLocaleDateString()} ~ {new Date(data.period_end).toLocaleDateString()}
            </p>
          </div>

          <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #e2e8f0' }}>
                <th style={{ padding: '8px', textAlign: 'left' }}>Month</th>
                <th style={{ padding: '8px', textAlign: 'right' }}>Revenue</th>
                <th style={{ padding: '8px', textAlign: 'right' }}>Cost</th>
                <th style={{ padding: '8px', textAlign: 'right' }}>Profit</th>
              </tr>
            </thead>
            <tbody>
              {data.months.map(m => (
                <tr key={m.month} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '8px' }}>{m.month}</td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>{fmt(m.revenue)}</td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>{fmt(m.cost)}</td>
                  <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: m.profit >= 0 ? '#059669' : '#dc2626' }}>{fmt(m.profit)}</td>
                </tr>
              ))}
              <tr style={{ borderTop: '2px solid #e2e8f0', fontWeight: 700 }}>
                <td style={{ padding: '8px' }}>Total</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{fmt(data.months.reduce((s, m) => s + m.revenue, 0))}</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{fmt(data.months.reduce((s, m) => s + m.cost, 0))}</td>
                <td style={{ padding: '8px', textAlign: 'right', color: data.months.reduce((s, m) => s + m.profit, 0) >= 0 ? '#059669' : '#dc2626' }}>
                  {fmt(data.months.reduce((s, m) => s + m.profit, 0))}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default IncomeStatement;
